// ============================================================================
// RHCancelamentoAdmissao_Fila.js
// Fila das solicitações de cancelamento de admissão.
//   • RH / Diretoria / Admin  → APROVAM ou REPROVAM as pendentes (com observação).
//   • Gerente / Gerente Plantão → leem só a PRÓPRIA loja (recorte no backend).
// Filtro por status. Renderizada pelo mini-hub RHCancelamentoAdmissao.
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import { ClipboardList, AlertCircle, CheckCircle2, ThumbsUp, ThumbsDown, X, Paperclip, FileText, RotateCcw } from 'lucide-react';
import { estilos, BadgeStatus } from '../../processoseletivo/psUI';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const COR = '#e11d48';

// Quem pode decidir (bate com PERFIL_DECIDE do backend + Admin).
const PERFIS_DECIDEM = ['RH', 'Diretoria', 'Admin'];

const fmtData = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
};
// DATETIME (vem como ISO do backend) → 'DD/MM/AAAA HH:MM' no fuso de Brasília.
const fmtDataHora = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(v); }
};
// Faltas/atestados vêm apurados AO VIVO pelo backend. `null` = SEM DADO no
// espelho de ponto (recém-admitido ainda fora do CSV mensal) — mostra "—", não 0.
const fmtNumero = (n) => (n === null || n === undefined ? '—' : n);

const selo = (status) => {
  if (status === 'PENDENTE') return <BadgeStatus tom="emProcesso">Pendente</BadgeStatus>;
  if (status === 'APROVADO') return <BadgeStatus tom="aprovado">Aprovado</BadgeStatus>;
  if (status === 'REPROVADO') return <BadgeStatus tom="reprovado">Reprovado</BadgeStatus>;
  return <BadgeStatus tom="neutro">{status}</BadgeStatus>;
};

// Resolve o tipo do pedido: usa o TIPO_CANCELAMENTO gravado; se for antigo (nulo,
// anterior à coluna), deriva do snapshot — faltou na própria data de admissão?
// (mesma regra do backend em cancelamento-admissao-regras.js).
const tipoDe = (s) => {
  let valor = s.TIPO_CANCELAMENTO;
  if (!valor) {
    const adm = String(s.DATA_ADMISSAO || '').slice(0, 10);
    const dias = String(s.DATAS_FALTA || '').split(',').map((d) => d.trim().slice(0, 10));
    valor = adm && dias.includes(adm) ? 'ADMISSAO' : 'EXPERIENCIA';
  }
  return valor === 'ADMISSAO'
    ? { valor, label: 'Cancelamento de admissão' }
    : { valor, label: 'Cancelar contrato de experiência' };
};

// Selo visual do tipo (ADMISSAO vermelho; EXPERIENCIA âmbar).
const seloTipo = (s) => {
  const t = tipoDe(s);
  const cores = t.valor === 'ADMISSAO' ? { bg: '#fee2e2', fg: '#b91c1c' } : { bg: '#fef3c7', fg: '#b45309' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: cores.bg, color: cores.fg, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {t.label}
    </span>
  );
};

const FILTROS = [
  { key: 'PENDENTE', label: 'Pendentes' },
  { key: '', label: 'Todas' },
  { key: 'APROVADO', label: 'Aprovadas' },
  { key: 'REPROVADO', label: 'Reprovadas' },
];

function CancelamentoFila() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const podeDecidir = PERFIS_DECIDEM.includes(perfilNoModulo(user?.perfil, 'rh') || '');

  const [status, setStatus] = useState('PENDENTE');
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Modal de decisão
  const [alvo, setAlvo] = useState(null);          // solicitação a decidir
  const [observacao, setObservacao] = useState('');
  // Anexo OPCIONAL que o RH pode juntar à decisão (aprovar ou reprovar) —
  // comprovante/documento de apoio. A maioria das decisões não gera papel.
  const [documento, setDocumento] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(null);
  // Confirmação da reabertura (some a decisão): dois cliques, para não desfazer
  // um lançamento por engano.
  const [confirmarReabrir, setConfirmarReabrir] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('authToken');
      const qs = status ? `?status=${status}` : '';
      const resp = await fetch(`/api/rh/cancelamento-admissao/solicitacoes${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Falha ao carregar solicitações.');
      setLinhas(await resp.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = (s) => { setAlvo(s); setObservacao(''); setDocumento(null); setFeedbackModal(null); setConfirmarReabrir(false); };
  const fechar = () => { if (!salvando) setAlvo(null); };

  // Abre o documento anexado pelo RH (rota autenticada → fetch+blob, nunca <a href>).
  const abrirDocumento = async (s) => {
    try {
      await abrirArquivoComToken(`/api/rh/cancelamento-admissao/solicitacoes/${s.ID}/arquivo`, s.DOCUMENTO_NOME || `decisao_${s.ID}`);
    } catch (e) {
      setFeedbackModal({ tipo: 'erro', msg: e.message });
    }
  };

  const decidir = async (decisao) => {
    if (!alvo || salvando) return;
    // ⚠️ OBRIGATÓRIA nos dois desfechos (16/09/2026): a loja fica esperando uma
    // resposta sobre uma PESSOA, e "reprovado" sem motivo não é resposta. A
    // mesma trava existe no servidor — esta aqui só evita a viagem.
    if (!observacao.trim()) {
      return setFeedbackModal({ tipo: 'erro', msg: 'Escreva a observação do RH — ela é obrigatória nesta demanda.' });
    }
    setSalvando(true);
    setFeedbackModal(null);
    try {
      const token = localStorage.getItem('authToken');
      // ⚠️ FormData, não JSON: a decisão pode levar um anexo (opcional). O
      //    'Content-Type' fica de fora de propósito — quem escreve o header (com
      //    o boundary) é o navegador; declará-lo à mão quebra o multipart.
      const corpo = new FormData();
      corpo.append('decisao', decisao);
      corpo.append('observacao', observacao.trim());
      if (documento) corpo.append('documento', documento);
      const resp = await fetch(`/api/rh/cancelamento-admissao/solicitacoes/${alvo.ID}/decisao`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: corpo,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'Erro ao registrar decisão.');
      setAlvo(null);
      await carregar();
    } catch (e) {
      setFeedbackModal({ tipo: 'erro', msg: e.message });
    } finally {
      setSalvando(false);
    }
  };

  // Reabre uma solicitação JÁ DECIDIDA: apaga a decisão e devolve à fila como
  // pendente, para o RH decidir de novo (conserto de erro de lançamento).
  const reabrir = async () => {
    if (!alvo || salvando) return;
    setSalvando(true);
    setFeedbackModal(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`/api/rh/cancelamento-admissao/solicitacoes/${alvo.ID}/reabrir`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'Erro ao reabrir a solicitação.');
      setAlvo(null);
      await carregar();
    } catch (e) {
      setFeedbackModal({ tipo: 'erro', msg: e.message });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ background: `${COR}15`, color: COR, padding: 12, borderRadius: 12, display: 'flex' }}>
          <ClipboardList size={24} />
        </div>
        <div>
          <h3 style={estilos.tituloSecao}>Solicitações de Término de Contrato de Experiência</h3>
          <p style={estilos.subtituloSecao}>
            {podeDecidir
              ? 'Aprove ou reprove os pedidos dos gerentes. Se aprovado, faça o término do contrato no sistema de RH.'
              : 'Acompanhe o andamento dos pedidos de término de contrato de experiência da sua loja.'}
          </p>
        </div>
      </div>

      {/* Filtro de status */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTROS.map((f) => {
          const on = f.key === status;
          return (
            <button
              key={f.key || 'todas'}
              onClick={() => setStatus(f.key)}
              style={{
                padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .2s',
                border: on ? `1px solid ${COR}` : '1px solid #e2e8f0',
                background: on ? '#fff1f2' : '#fff', color: on ? COR : '#64748b',
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ padding: 24, color: '#64748b' }}>Carregando…</div>
      ) : erro ? (
        <div style={{ padding: 24, color: COR, display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={18} /> {erro}</div>
      ) : linhas.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Nenhuma solicitação neste filtro.</div>
      ) : (
        <div style={{ ...estilos.tabelaWrap, overflowX: 'auto' }}>
          <table style={{ ...estilos.tabela, minWidth: 1020 }}>
            <thead>
              <tr>
                <th style={estilos.th}>Funcionário</th>
                <th style={estilos.th}>Loja</th>
                <th style={estilos.th}>Fim 1ª Exp.</th>
                <th style={estilos.th}>Fim 2ª Exp.</th>
                <th style={{ ...estilos.th, textAlign: 'right' }}>Faltas</th>
                <th style={{ ...estilos.th, textAlign: 'right' }}>Atestados</th>
                <th style={estilos.th}>Tipo</th>
                <th style={estilos.th}>Solicitante</th>
                <th style={estilos.th}>Data do pedido</th>
                <th style={estilos.th}>Status</th>
                <th style={{ ...estilos.th, textAlign: 'right' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((s) => (
                <tr key={s.ID}>
                  <td style={estilos.td}>
                    <strong>{s.NOME_PESSOA}</strong>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{s.NOME_CARGO || '—'}</div>
                  </td>
                  <td style={estilos.td}>{s.LOJA_EXIBICAO || s.CODIGO_LOCALIZACAO || '—'}</td>
                  <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>{fmtData(s.EXP1)}</td>
                  <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>{fmtData(s.EXP2)}</td>
                  <td style={{ ...estilos.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNumero(s.FALTAS)}</td>
                  <td style={{ ...estilos.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNumero(s.ATESTADOS)}</td>
                  <td style={estilos.td}>{seloTipo(s)}</td>
                  <td style={estilos.td}>{s.SOLICITANTE_NOME || '—'}</td>
                  <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>{fmtDataHora(s.DATA_SOLICITACAO)}</td>
                  <td style={estilos.td}>
                    {selo(s.STATUS)}
                    {s.STATUS !== 'PENDENTE' && s.DECIDIDO_POR_NOME && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>por {s.DECIDIDO_POR_NOME}</div>
                    )}
                  </td>
                  <td style={{ ...estilos.td, textAlign: 'right' }}>
                    {podeDecidir && s.STATUS === 'PENDENTE' ? (
                      <button
                        onClick={() => abrir(s)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                          border: 'none', background: COR, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        Analisar
                      </button>
                    ) : (
                      <button
                        onClick={() => abrir(s)}
                        style={{
                          padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
                          color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        Ver
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de decisão / detalhe */}
      {alvo && (
        <div
          onClick={fechar}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(15,23,42,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Solicitação de Término de Contrato de Experiência</h3>
              <button onClick={fechar} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>

            {/* Resumo */}
            <div style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '14px 16px', marginBottom: 16, fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
              <div><strong style={{ color: '#0f172a' }}>{alvo.NOME_PESSOA}</strong> · {alvo.NOME_CARGO || '—'}</div>
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>Loja {alvo.LOJA_EXIBICAO || alvo.CODIGO_LOCALIZACAO} · admitido em {fmtData(alvo.DATA_ADMISSAO)}</div>
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>Fim da experiência: 1ª {fmtData(alvo.EXP1)} · 2ª {fmtData(alvo.EXP2)}</div>
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNumero(alvo.FALTAS)} falta(s) · {fmtNumero(alvo.ATESTADOS)} atestado(s) desde a admissão</div>
              <div style={{ marginTop: 8 }}>{seloTipo(alvo)}</div>
              <div style={{ marginTop: 8 }}>Pedido por <strong>{alvo.SOLICITANTE_NOME || '—'}</strong> em {fmtDataHora(alvo.DATA_SOLICITACAO)}</div>
            </div>

            {/* Justificativa do gerente */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 4 }}>Justificativa do gerente</div>
              <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                {alvo.JUSTIFICATIVA || '—'}
              </div>
            </div>

            {/* Se já decidida: mostra o desfecho. Se pendente e pode decidir: form. */}
            {alvo.STATUS !== 'PENDENTE' ? (
              <div style={{ marginBottom: 8 }}>
                <div style={{ marginBottom: 8 }}>{selo(alvo.STATUS)} <span style={{ fontSize: 13, color: '#64748b' }}>por {alvo.DECIDIDO_POR_NOME || '—'} em {fmtDataHora(alvo.DATA_DECISAO)}</span></div>
                {alvo.OBSERVACAO_RH && (
                  <div style={{ fontSize: 13, color: '#475569' }}><strong>Observação do RH:</strong> {alvo.OBSERVACAO_RH}</div>
                )}
                {/* Documento anexado pela decisão — só aparece quando existe. */}
                {alvo.DOCUMENTO_URL && (
                  <button
                    onClick={() => abrirDocumento(alvo)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12,
                      padding: '6px 12px', borderRadius: 6, border: `1px solid ${COR}`,
                      background: '#fff', color: COR, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    <FileText size={14} /> Abrir documento
                  </button>
                )}
                {feedbackModal && feedbackModal.tipo === 'erro' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>
                    <AlertCircle size={18} /> {feedbackModal.msg}
                  </div>
                )}

                {/* Reabrir — só quem decide. Desfaz a decisão (por erro de
                    lançamento) e devolve o pedido para a fila como pendente.
                    Dois cliques, porque apaga a decisão registrada. */}
                {podeDecidir && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                    {!confirmarReabrir ? (
                      <button
                        onClick={() => setConfirmarReabrir(true)}
                        disabled={salvando}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                          border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700,
                          cursor: salvando ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <RotateCcw size={15} /> Reabrir solicitação
                      </button>
                    ) : (
                      <div>
                        <div style={{ fontSize: 13, color: '#b45309', marginBottom: 10 }}>
                          Isso <strong>apaga a decisão</strong> e devolve o pedido à fila como pendente. Confirma?
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={reabrir}
                            disabled={salvando}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                              border: 'none', background: '#b45309', color: '#fff', fontSize: 13, fontWeight: 700,
                              cursor: salvando ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <RotateCcw size={15} /> {salvando ? 'Reabrindo…' : 'Sim, reabrir'}
                          </button>
                          <button
                            onClick={() => setConfirmarReabrir(false)}
                            disabled={salvando}
                            style={{
                              padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
                              color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : podeDecidir ? (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  Observação do RH <span style={{ color: COR }}>*</span>
                </label>
                <textarea
                  style={{ width: '100%', minHeight: 72, padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, color: '#0f172a', boxSizing: 'border-box', resize: 'vertical' }}
                  placeholder="O que foi decidido sobre a pessoa, e por quê. A loja lê este texto."
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                />

                {/* Anexo OPCIONAL — documento de apoio da decisão (aprovar ou
                    reprovar). PDF ou imagem. O File fica no estado do pai. */}
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginTop: 16, marginBottom: 6 }}>
                  Anexar documento <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span>
                </label>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${documento ? '#6ee7b7' : '#e2e8f0'}`,
                  background: documento ? '#f0fdf4' : '#fff',
                  color: documento ? '#047857' : '#64748b', fontSize: 14, fontWeight: 600,
                }}>
                  {documento ? <CheckCircle2 size={16} /> : <Paperclip size={16} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {documento ? documento.name : 'Selecionar PDF ou imagem'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) => setDocumento(e.target.files[0] || null)}
                    style={{ display: 'none' }}
                  />
                </label>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>PDF ou imagem, até 15 MB.</div>

                {feedbackModal && feedbackModal.tipo === 'erro' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>
                    <AlertCircle size={18} /> {feedbackModal.msg}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                  <button
                    onClick={() => decidir('REPROVADO')}
                    disabled={salvando}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 8,
                      border: '1px solid #fecaca', background: '#fff', color: '#be123c', fontWeight: 700, fontSize: 14,
                      cursor: salvando ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ThumbsDown size={16} /> Reprovar
                  </button>
                  <button
                    onClick={() => decidir('APROVADO')}
                    disabled={salvando}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, border: 'none',
                      background: '#047857', color: '#fff', fontWeight: 700, fontSize: 14, cursor: salvando ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ThumbsUp size={16} /> {salvando ? 'Salvando…' : 'Aprovar'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}>
                <CheckCircle2 size={16} /> Aguardando decisão do RH.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CancelamentoFila;
